import { useState } from "react";
import type { ImeiOut } from "@/lib/api";
import ImeiChip from "@/ui/ImeiChip";

/**
 * Los IMEIs de un caso en una lista: los primeros `max` y un «+N más» que
 * despliega el resto ahí mismo.
 *
 * Antes el resto vivía en el `title` del «+N más»: un tooltip, y en el celular
 * (donde no existe pasar el mouse) esos IMEIs eran invisibles. Ahora se tocan.
 *
 * ⚠️ Es un `<button>`: NO lo montes dentro de un `<a>`/`<Link>` (un control
 * dentro de un enlace es HTML inválido y rompe la navegación). En las tarjetas
 * del celular la fila de IMEIs va fuera del enlace. En una fila de tabla con
 * `onClick` sí se puede: el botón corta la propagación.
 */
export default function ImeisLista({
  imeis,
  max = 3,
  className = "",
}: {
  imeis: ImeiOut[];
  max?: number;
  className?: string;
}) {
  const [todos, setTodos] = useState(false);
  if (imeis.length === 0) return null;
  const ocultos = imeis.length - max;
  const visibles = todos || ocultos <= 0 ? imeis : imeis.slice(0, max);
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`}>
      {visibles.map((i) => (
        <ImeiChip key={i.id} imei={i} />
      ))}
      {ocultos > 0 && (
        <button
          type="button"
          aria-expanded={todos}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setTodos((v) => !v);
          }}
          className="rounded px-1.5 py-0.5 text-[10px] text-accent-300 hover:bg-ink-800 hover:text-accent-200"
        >
          {todos ? "ver menos" : `+${ocultos} más`}
        </button>
      )}
    </div>
  );
}
